import React, { useState, useEffect } from 'react';
import { FileText, Download, ChevronLeft, ChevronRight } from 'lucide-react';
import { Link } from 'react-router-dom';

const PatientAccountsPage = () => {
  // Set page title
  useEffect(() => {
    document.title = 'MediFusion - Patient Admin';
  }, []);
  const [currentPage, setCurrentPage] = useState(1);
  const entriesPerTable = 10;
  const totalEntriesPerPage = entriesPerTable * 2; // 20 total entries per page

  // Mock account data - expanded to show pagination
  const accountData = [
    {
      id: 1,
      date: '16/09/2024',
      description: 'Telehealth Consultation - Dr. Michael Barnes',
      invoiceUrl: '/invoices/invoice-001.pdf'
    },
    {
      id: 2,
      date: '15/09/2024',
      description: 'Follow-up Appointment - Dr. Sarah Johnson',
      invoiceUrl: '/invoices/invoice-002.pdf'
    },
    {
      id: 3,
      date: '14/09/2024',
      description: 'General Check-up - Dr. Michael Barnes',
      invoiceUrl: '/invoices/invoice-003.pdf'
    },
    {
      id: 4,
      date: '13/09/2024',
      description: 'Specialist Consultation - Dr. Robert Chen',
      invoiceUrl: '/invoices/invoice-004.pdf'
    },
    {
      id: 5,
      date: '12/09/2024',
      description: 'Telehealth Follow-up - Dr. Sarah Johnson',
      invoiceUrl: '/invoices/invoice-005.pdf'
    },
    {
      id: 6,
      date: '11/09/2024',
      description: 'Emergency Consultation - Dr. Michael Barnes',
      invoiceUrl: '/invoices/invoice-006.pdf'
    },
    {
      id: 7,
      date: '10/09/2024',
      description: 'Routine Check-up - Dr. Emily Davis',
      invoiceUrl: '/invoices/invoice-007.pdf'
    },
    {
      id: 8,
      date: '09/09/2024',
      description: 'Specialist Review - Dr. Robert Chen',
      invoiceUrl: '/invoices/invoice-008.pdf'
    },
    {
      id: 9,
      date: '08/09/2024',
      description: 'Telehealth Consultation - Dr. Michael Barnes',
      invoiceUrl: '/invoices/invoice-009.pdf'
    },
    {
      id: 10,
      date: '07/09/2024',
      description: 'Follow-up Appointment - Dr. Sarah Johnson',
      invoiceUrl: '/invoices/invoice-010.pdf'
    },
    {
      id: 11,
      date: '06/09/2024',
      description: 'General Check-up - Dr. Michael Barnes',
      invoiceUrl: '/invoices/invoice-011.pdf'
    },
    {
      id: 12,
      date: '05/09/2024',
      description: 'Specialist Consultation - Dr. Robert Chen',
      invoiceUrl: '/invoices/invoice-012.pdf'
    },
    {
      id: 13,
      date: '04/09/2024',
      description: 'Telehealth Follow-up - Dr. Sarah Johnson',
      invoiceUrl: '/invoices/invoice-013.pdf'
    },
    {
      id: 14,
      date: '03/09/2024',
      description: 'Emergency Consultation - Dr. Michael Barnes',
      invoiceUrl: '/invoices/invoice-014.pdf'
    },
    {
      id: 15,
      date: '02/09/2024',
      description: 'Routine Check-up - Dr. Emily Davis',
      invoiceUrl: '/invoices/invoice-015.pdf'
    },
    {
      id: 16,
      date: '01/09/2024',
      description: 'Specialist Review - Dr. Robert Chen',
      invoiceUrl: '/invoices/invoice-016.pdf'
    },
    {
      id: 17,
      date: '31/08/2024',
      description: 'Telehealth Consultation - Dr. Michael Barnes',
      invoiceUrl: '/invoices/invoice-017.pdf'
    },
    {
      id: 18,
      date: '30/08/2024',
      description: 'Follow-up Appointment - Dr. Sarah Johnson',
      invoiceUrl: '/invoices/invoice-018.pdf'
    },
    {
      id: 19,
      date: '29/08/2024',
      description: 'General Check-up - Dr. Michael Barnes',
      invoiceUrl: '/invoices/invoice-019.pdf'
    },
    {
      id: 20,
      date: '28/08/2024',
      description: 'Specialist Consultation - Dr. Robert Chen',
      invoiceUrl: '/invoices/invoice-020.pdf'
    },
    {
      id: 21,
      date: '27/08/2024',
      description: 'Telehealth Follow-up - Dr. Sarah Johnson',
      invoiceUrl: '/invoices/invoice-021.pdf'
    },
    {
      id: 22,
      date: '26/08/2024',
      description: 'Emergency Consultation - Dr. Michael Barnes',
      invoiceUrl: '/invoices/invoice-022.pdf'
    },
    {
      id: 23,
      date: '25/08/2024',
      description: 'Routine Check-up - Dr. Emily Davis',
      invoiceUrl: '/invoices/invoice-023.pdf'
    },
    {
      id: 24,
      date: '24/08/2024',
      description: 'Specialist Review - Dr. Robert Chen',
      invoiceUrl: '/invoices/invoice-024.pdf'
    }
  ];

  // Calculate pagination
  const totalPages = Math.ceil(accountData.length / totalEntriesPerPage);
  const startIndex = (currentPage - 1) * totalEntriesPerPage;
  const endIndex = startIndex + totalEntriesPerPage;
  const currentPageData = accountData.slice(startIndex, endIndex);

  // Split data for two tables
  const firstTableData = currentPageData.slice(0, entriesPerTable);
  const secondTableData = currentPageData.slice(entriesPerTable, entriesPerTable * 2);

  const handleViewInvoice = (invoiceUrl) => {
    // In a real app, this would open the invoice in a new tab or download it
    console.log('Viewing invoice:', invoiceUrl);
    window.open(invoiceUrl, '_blank');
  };

  const handleDownloadInvoice = (invoiceUrl) => {
    // In a real app, this would trigger a download
    console.log('Downloading invoice:', invoiceUrl);
    const link = document.createElement('a');
    link.href = invoiceUrl;
    link.download = invoiceUrl.split('/').pop();
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const handlePageChange = (page) => {
    setCurrentPage(page);
  };

  const renderTable = (data, tableId) => (
    <div className="bg-white rounded-lg shadow-md p-4">
      <div className="overflow-x-auto">
        <table className="w-full">
          <thead>
            <tr className="border-b border-gray-200">
              <th className="text-center py-2 px-2 text-sm font-medium text-gray-700 w-1/4">Date</th>
              <th className="text-center py-2 px-2 text-sm font-medium text-gray-700 w-1/2">Description</th>
              <th className="text-center py-2 px-2 text-sm font-medium text-gray-700 w-1/4">View / Download Invoice</th>
            </tr>
          </thead>
          <tbody>
            {data.map((item) => (
              <tr key={`${tableId}-${item.id}`} className="border-b border-gray-100 hover:bg-gray-50">
                <td className="py-2 px-2 text-sm text-gray-600 text-center">{item.date}</td>
                <td className="py-2 px-2 text-sm text-gray-600 text-center">{item.description}</td>
                <td className="py-2 px-2">
                  <div className="flex space-x-2 justify-center">
                    <button
                      onClick={() => handleViewInvoice(item.invoiceUrl)}
                      className="text-blue-600 hover:text-blue-800 transition-colors duration-200 transform hover:scale-110 active:scale-95"
                      title="View Invoice"
                    >
                      <FileText className="h-4 w-4" />
                    </button>
                    <button
                      onClick={() => handleDownloadInvoice(item.invoiceUrl)}
                      className="text-red-600 hover:text-red-800 transition-colors duration-200 transform hover:scale-110 active:scale-95"
                      title="Download Invoice"
                    >
                      <Download className="h-4 w-4" />
                    </button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );

  const renderPagination = () => {
    const pages = [];
    const maxVisiblePages = 5;
    let startPage = Math.max(1, currentPage - Math.floor(maxVisiblePages / 2));
    let endPage = Math.min(totalPages, startPage + maxVisiblePages - 1);

    if (endPage - startPage + 1 < maxVisiblePages) {
      startPage = Math.max(1, endPage - maxVisiblePages + 1);
    }

    for (let i = startPage; i <= endPage; i++) {
      pages.push(i);
    }

    return (
      <div className="flex items-center justify-center space-x-2 mt-6">
        <button
          onClick={() => handlePageChange(currentPage - 1)}
          disabled={currentPage === 1}
          className="flex items-center px-3 py-2 text-sm font-medium text-gray-500 bg-white border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors duration-200"
        >
          <ChevronLeft className="h-4 w-4 mr-1" />
          Previous
        </button>

        {startPage > 1 && (
          <>
            <button
              onClick={() => handlePageChange(1)}
              className="px-3 py-2 text-sm font-medium text-gray-500 bg-white border border-gray-300 rounded-md hover:bg-gray-50 transition-colors duration-200"
            >
              1
            </button>
            {startPage > 2 && (
              <span className="px-3 py-2 text-sm text-gray-500">...</span>
            )}
          </>
        )}

        {pages.map((page) => (
          <button
            key={page}
            onClick={() => handlePageChange(page)}
            className={`px-3 py-2 text-sm font-medium rounded-md transition-colors duration-200 ${
              currentPage === page
                ? 'bg-blue-600 text-white border border-blue-600'
                : 'text-gray-500 bg-white border border-gray-300 hover:bg-gray-50'
            }`}
          >
            {page}
          </button>
        ))}

        {endPage < totalPages && (
          <>
            {endPage < totalPages - 1 && (
              <span className="px-3 py-2 text-sm text-gray-500">...</span>
            )}
            <button
              onClick={() => handlePageChange(totalPages)}
              className="px-3 py-2 text-sm font-medium text-gray-500 bg-white border border-gray-300 rounded-md hover:bg-gray-50 transition-colors duration-200"
            >
              {totalPages}
            </button>
          </>
        )}

        <button
          onClick={() => handlePageChange(currentPage + 1)}
          disabled={currentPage === totalPages}
          className="flex items-center px-3 py-2 text-sm font-medium text-gray-500 bg-white border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors duration-200"
        >
          Next
          <ChevronRight className="h-4 w-4 ml-1" />
        </button>
      </div>
    );
  };

  return (
    <div className="flex gap-6">
      {/* Left Column - 20% */}
      <div className="w-1/5 bg-gray-100 min-h-screen p-4">
        <div className="space-y-2">
          <Link
            to="/patient/admin/mydetails"
            className="w-full text-left px-4 py-3 rounded-md text-sm font-medium transition-all duration-200 bg-gray-200 text-gray-700 hover:bg-gray-300 h-12 flex items-center focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 transform hover:scale-[1.02] active:scale-[0.98] hover:shadow-sm"
          >
            My Details
          </Link>
          <Link
            to="/patient/admin/preferences"
            className="w-full text-left px-4 py-3 rounded-md text-sm font-medium transition-all duration-200 bg-gray-200 text-gray-700 hover:bg-gray-300 h-12 flex items-center focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 transform hover:scale-[1.02] active:scale-[0.98] hover:shadow-sm"
          >
            Preferences
          </Link>
          <button
            className="w-full text-left px-4 py-3 rounded-md text-sm font-medium transition-all duration-200 bg-blue-600 text-white h-12 flex items-center focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 transform hover:scale-[1.02] active:scale-[0.98] shadow-md"
          >
            Accounts
          </button>
          <Link
            to="/patient/admin/bookings"
            className="w-full text-left px-4 py-3 rounded-md text-sm font-medium transition-all duration-200 bg-gray-200 text-gray-700 hover:bg-gray-300 h-12 flex items-center focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 transform hover:scale-[1.02] active:scale-[0.98] hover:shadow-sm"
          >
            Bookings
          </Link>
        </div>
      </div>

      {/* Right Column - 80% */}
      <div className="w-4/5 bg-gray-100 p-4">
        <div className="grid grid-cols-2 gap-6">
          {/* First Account Table */}
          {renderTable(firstTableData, 'first')}

          {/* Second Account Table */}
          {renderTable(secondTableData, 'second')}
        </div>

        {/* Pagination */}
        {renderPagination()}
      </div>
    </div>
  );
};

export default PatientAccountsPage; 